import React from 'react';
import { Layout } from 'fronton-react';
import { RemoteCarouselWithBasket, RootLayout } from 'common/components';
import {
  Pdp,
  ProductComposition,
  ProductDescription,
  ProductInfo,
  ProductMedia,
  ProductStickyPanel,
} from 'common/components/pdp';
import { useLocation } from '@okapi/router-react';
import {
  COLLECTIONS_STORE_NAME,
  selectAreAllCollectionSizesUnavailable,
  selectCollectionInfo,
  selectIsCollectionAvailable,
  VariantAxisPoint,
  VariantAxisProduct,
} from 'modules/collections';
import { RelatedService } from 'modules/related-services';
import { useEffectOnce } from 'common/hooks';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { analyticsUtils } from 'utils/analytics-utils';
import { removeSpecialCharacters } from 'utils/format';
import { getBasketHref } from 'utils/get-basket-href';
import { addToCartGAEvent, showMoreClickGAEvent } from 'utils';
import { CategoryCardSection } from './components';
import {
  KITCHEN_PDP_ELEMENT_BLOCK,
  KITCHEN_PDP_LANDING_NAME,
  MODIFIED_KITCHEN_PDP_BREADCRUMBS,
  MODIFIED_KITCHEN_PDP_CORNER_BREADCRUMBS,
} from './kitchen-pdp-constants';
import {
  axisPointClickGAEvent,
  previewClickGAEvent,
  serviceClickGAEvent,
  serviceShowGAEvent,
} from './kitchen-pdp-utils';

export const KitchenPdp = () => {
  const [path] = useLocation();
  const { collections } = useStoreon<State>(COLLECTIONS_STORE_NAME);
  const areAllSizesUnavailable = selectAreAllCollectionSizesUnavailable(collections);
  const isCollectionAvailable = selectIsCollectionAvailable(collections);
  const collectionInfo = selectCollectionInfo(collections);
  const activePoint = collectionInfo?.variants?.activePoint || ({} as VariantAxisProduct);
  const basketHref = getBasketHref({
    products: activePoint.products || [],
    totalUrlLabel: activePoint.name,
  });

  const isCorner = path.includes('uglovye');
  const modifiedBreadcrumbs = isCorner
    ? MODIFIED_KITCHEN_PDP_CORNER_BREADCRUMBS
    : MODIFIED_KITCHEN_PDP_BREADCRUMBS;

  const productName = removeSpecialCharacters(activePoint.displayedName);

  useEffectOnce(() => {
    if (activePoint) {
      analyticsUtils.sendEvent('GA:detail', {
        elementType: 'page',
        elementBlock: KITCHEN_PDP_ELEMENT_BLOCK, // блок, в котором расположен элемент
        komplektyId: activePoint.productId, // идентификатор комплекта
        productName, // название комплекта
        productId: activePoint.productId, // идентификатор комплекта
        productPrice: activePoint.price.price, // показанная стоимость комплекта
      });
    }
  });

  const handleAddToCartClickSticky = () => {
    addToCartGAEvent(activePoint, KITCHEN_PDP_ELEMENT_BLOCK, 'sticky-menu');
  };

  const handleAddToCartClick = () => {
    addToCartGAEvent(activePoint, KITCHEN_PDP_ELEMENT_BLOCK);
  };

  const handleOnShowMoreClick = (buttonName: string, elementName: string) => {
    showMoreClickGAEvent(buttonName, elementName, activePoint.productId, KITCHEN_PDP_ELEMENT_BLOCK);
  };

  const handleAxisPointClick = (clickedPoint: VariantAxisPoint) => {
    axisPointClickGAEvent(clickedPoint, activePoint.productId);
  };

  const handleServiceClick = (service: RelatedService, position: number) => {
    serviceClickGAEvent(service, position, activePoint.productId);
  };

  const handleServiceShowEvent = (service: RelatedService, position: number) => {
    serviceShowGAEvent(service, position, activePoint.productId);
  };

  const handlePreviewClick = (index: number) => {
    previewClickGAEvent(activePoint, index);
  };

  return (
    <RootLayout currentPage={path} customBreadcrumbs={modifiedBreadcrumbs}>
      <Pdp
        productMedia={
          <ProductMedia
            collectionInfo={activePoint}
            landingName={KITCHEN_PDP_LANDING_NAME}
            onPreviewClick={handlePreviewClick}
          />
        }
        productInfo={
          <ProductInfo
            areAllSizesUnavailable={areAllSizesUnavailable}
            basketHref={basketHref}
            buttonLabel="Купить"
            disclaimer={
              collectionInfo.tabletop
                ? 'Без учета бытовой техники'
                : 'Без учёта бытовой техники и\u00A0столешницы'
            }
            infoNote={[
              'На\u00A0фотографии представлен демонстрационный образец.',
              'Для проверки наличия товаров добавьте гарнитур в\u00A0корзину.',
            ]}
            onAxisPointClick={handleAxisPointClick}
            onAddToCartClick={handleAddToCartClick}
            onServiceClick={handleServiceClick}
            onServiceShowEvent={handleServiceShowEvent}
          />
        }>
        <ProductStickyPanel
          basketHref={basketHref}
          buttonLabel="Купить"
          collectionInfo={activePoint}
          isCollectionAvailable={isCollectionAvailable}
          landingName={KITCHEN_PDP_LANDING_NAME}
          onAddToCartClick={handleAddToCartClickSticky}
        />
        <Layout>
          {!areAllSizesUnavailable && isCollectionAvailable && (
            <ProductComposition
              collectionInfo={activePoint}
              productDivision="Кухни"
              elementBlock={KITCHEN_PDP_ELEMENT_BLOCK}
              onShowMoreClick={handleOnShowMoreClick}
            />
          )}
          <ProductDescription
            description={activePoint.description || collectionInfo.description}
            onShowMoreClick={handleOnShowMoreClick}
          />
        </Layout>
        <RemoteCarouselWithBasket
          elementBlock={`${productName} / Не забудьте купить`}
          title="Не забудьте купить"
          productIdList={activePoint.offerProductsData}
          remoteId="doNotForgetToBuy"
        />
        <Layout>
          <CategoryCardSection productId={activePoint.productId} />
        </Layout>
      </Pdp>
    </RootLayout>
  );
};
